// Phase 1A Phase 9C.11 — CouncilPreviewPage.
//
// /council route entrypoint. Replaces the generic
// <ModePlaceholderPage mode="council" /> scaffold with a dedicated
// Council preview that truthfully explains the future SIKA goal-
// intelligence layer in Phase-1A-preview framing.
//
// Pure presentation: no data hooks, no Supabase reads, no RPCs, no
// forms, no inputs, no fake projections. Every rendered string is a
// static deterministic phrase authored to satisfy the doctrine and
// the 9C.11 forbidden-vocab regression test.

import { useNavigate } from "react-router-dom";

import { Icon } from "../../components/icons/Icon.tsx";
import { CouncilShell } from "../../modes/council/CouncilShell.tsx";
import { MODE_ROUTE_PREFIXES } from "../../modes/mode-config.ts";
import * as s from "../../modes/council/council-shell.styles.ts";
import { pillInfoOutline, pillNeutral } from "../../styles/status-pills.ts";
import { UnifiedShell, type UnifiedMode } from "../../shell/index.ts";

const DESCRIPTION =
  "The Council will become STRIKZA's private SIKA room for goal planning, goal simulation, and disciplined bankroll planning. None of that ships in Phase-1A.";

interface FutureModule {
  name: string;
  hint: string;
}

const FUTURE_MODULES: readonly FutureModule[] = [
  {
    name: "SIKA Conversation",
    hint: "Plain-language goal capture. Not live yet.",
  },
  {
    name: "Goal Planner",
    hint: "Weeks-and-stakes draft from a stated goal. Not live yet.",
  },
  {
    name: "Goal Simulator",
    hint: "Project chain progression against a goal. Not live yet.",
  },
];

const TRUTH_ITEMS: readonly string[] = [
  "No SIKA conversations.",
  "No drafted plans.",
  "No goal data.",
  "No automatic betting or staking.",
  "No financial or investment advice.",
  "No live AI runtime.",
];

const SAFETY_FOOTER = "Phase-1A preview only. The Council ships when the SIKA layer ships.";

export function CouncilPreviewPage(): JSX.Element {
  const navigate = useNavigate();

  function handleNavigate(next: UnifiedMode): void {
    navigate(MODE_ROUTE_PREFIXES[next]);
  }

  return (
    <UnifiedShell
      mode="council"
      contextEyebrow="THE COUNCIL"
      contextTitle="SIKA goal-intelligence preview"
      safetyLabel="PHANTOM · SAFE MODE"
      accountLabel="1A"
      onNavigate={handleNavigate}
    >
      <CouncilShell
        eyebrow="The Council"
        title="SIKA goal-intelligence preview"
        description={DESCRIPTION}
      >
        {/* Goal-state empty card */}
        <section style={s.card} aria-label="Goal state">
          <span style={s.headerLead}>
            <Icon name="mode.daily_intelligence" size="sm" decorative />
            <span style={s.cardEyebrow}>Goal state</span>
          </span>
          <span style={pillInfoOutline}>NOT LIVE YET</span>
          <p style={s.italicPlaceholder}>No goal yet.</p>
          <p style={s.cardBody}>
            Goal data is not stored here yet. Manual planning only. The Council preview surface does
            not capture, persist, or analyse goals in Phase-1A.
          </p>
        </section>

        {/* Future modules card */}
        <section style={s.card} aria-label="Future modules">
          <span style={s.headerLead}>
            <Icon name="process.sample_valid" size="sm" decorative />
            <span style={s.cardEyebrow}>Future modules</span>
          </span>
          <h2 style={s.cardTitle}>What will live in The Council</h2>
          <p style={s.cardBody}>
            Each module below is reserved. Names and descriptions are deterministic placeholders; no
            module is wired to any data source today.
          </p>
          <div style={s.moduleList} aria-label="Reserved Council modules">
            {FUTURE_MODULES.map((m) => (
              <div key={m.name} style={s.moduleRow}>
                <div style={s.moduleHead}>
                  <span style={s.moduleName}>{m.name}</span>
                  <span style={pillNeutral}>Reserved</span>
                </div>
                <span style={s.moduleHint}>{m.hint}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Truth contract card */}
        <section style={s.card} aria-label="Truth contract">
          <span style={s.headerLead}>
            <Icon name="process.process_warning" size="sm" decorative />
            <span style={s.cardEyebrow}>Truth contract</span>
          </span>
          <p style={s.cardBody}>
            The Council preview does not yet do any of the following. The list below is the
            authoritative Phase-1A boundary.
          </p>
          <ul style={s.truthList}>
            {TRUTH_ITEMS.map((item) => (
              <li key={item} style={s.truthItem}>
                {item}
              </li>
            ))}
          </ul>
        </section>

        <span style={s.safetyLine}>{SAFETY_FOOTER}</span>
      </CouncilShell>
    </UnifiedShell>
  );
}
